import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Long-press / right-click menu for a chat message.
 *
 * Rendered through a portal into document.body for a specific reason: the chat
 * panel in CustomerDashboardLayout carries `animate-slideIn`, whose `forwards`
 * fill mode leaves `transform: translateX(0)` on the element permanently. Any
 * non-`none` transform makes that element the containing block for
 * `position: fixed` descendants, so the menu was being positioned against the
 * panel rather than the viewport and then clipped by the panel's
 * `overflow-hidden`. A portal moves the menu outside that subtree entirely.
 *
 * Responsive behaviour matches WhatsApp:
 *  - narrow screens  -> bottom sheet, full width, safe-area aware
 *  - wide screens    -> floating menu anchored to the pointer, clamped to the viewport
 */

const MOBILE_BREAKPOINT = 768;
const MENU_WIDTH = 208;
const EDGE_MARGIN = 8;

export default function MessageActionMenu({ open, x, y, isOwn, onClose, children }) {
  const menuRef = useRef(null);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT
  );
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Close on Escape, and prevent the page behind from scrolling while open.
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  /**
   * Entrance is driven by state + a CSS transition rather than a keyframe
   * animation. A keyframe animation determines the element's position only while
   * it actually runs; if the browser never advances its clock (background or
   * throttled tab), the element stays stranded on the `from` frame — for the
   * bottom sheet that means translateY(100%), i.e. entirely below the viewport.
   * Toggling a class from state guarantees the resting position either way, and
   * the transition is then purely decorative.
   */
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (!open) {
      setEntered(false);
      return undefined;
    }
    // A timer rather than requestAnimationFrame: rAF is tied to the paint clock
    // and never fires in a throttled or non-painting tab, which would leave the
    // sheet stranded off-screen. Timers still fire, so the resting position is
    // reached either way.
    const timer = setTimeout(() => setEntered(true), 0);
    return () => clearTimeout(timer);
  }, [open, isMobile]);

  // Measure the real menu instead of assuming a height, so a menu with more
  // items never runs off the bottom of the screen.
  useLayoutEffect(() => {
    if (!open || isMobile) return;

    const el = menuRef.current;
    const height = el?.offsetHeight || 280;
    const width = el?.offsetWidth || MENU_WIDTH;

    // Own messages open leftwards from the pointer, mirroring WhatsApp.
    let left = isOwn ? x - width : x;
    left = Math.max(EDGE_MARGIN, Math.min(left, window.innerWidth - width - EDGE_MARGIN));

    // Flip above the pointer when there is not enough room below.
    let top = y;
    if (top + height > window.innerHeight - EDGE_MARGIN) {
      top = Math.max(EDGE_MARGIN, y - height);
    }
    top = Math.max(EDGE_MARGIN, Math.min(top, window.innerHeight - height - EDGE_MARGIN));

    setPosition({ top, left });
  }, [open, x, y, isOwn, isMobile]);

  if (!open) return null;

  const backdrop = (
    <div
      className={`fixed inset-0 z-[998] ${isMobile ? 'bg-black/40' : ''}`}
      onClick={onClose}
      data-testid="message-menu-backdrop"
    />
  );

  const menu = isMobile ? (
    <div
      // Distinct keys force a remount when switching layouts, so the entrance
      // animation replays instead of React reusing the previous node.
      key="sheet"
      ref={menuRef}
      role="menu"
      data-testid="message-action-menu"
      className={`fixed inset-x-0 bottom-0 z-[999] rounded-t-2xl bg-wa-panel shadow-2xl
                 border-t border-wa-border py-2
                 pb-[max(0.5rem,env(safe-area-inset-bottom))]
                 transition-transform duration-200 ease-out
                 motion-reduce:transition-none
                 ${entered ? 'translate-y-0' : 'translate-y-full'}`}
    >
      {/* Grab handle, as on WhatsApp's mobile action sheet. */}
      <div className="mx-auto mb-1 h-1 w-9 rounded-full bg-wa-border" />
      {children}
    </div>
  ) : (
    <div
      key="floating"
      ref={menuRef}
      role="menu"
      data-testid="message-action-menu"
      className={`fixed z-[999] min-w-[208px] max-w-[calc(100vw-16px)] rounded-lg
                 bg-wa-panel shadow-[0_2px_10px_rgba(11,20,26,0.28)] py-1.5
                 overflow-hidden origin-top-left
                 transition-[opacity,transform] duration-100 ease-out
                 motion-reduce:transition-none
                 ${entered ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
      style={{ top: position.top, left: position.left }}
    >
      {children}
    </div>
  );

  return createPortal(
    <>
      {backdrop}
      {menu}
    </>,
    document.body
  );
}

/** Single row inside the menu, styled to WhatsApp's action list. */
export function MessageActionItem({ icon, label, onClick, destructive = false, testId }) {
  return (
    <button
      role="menuitem"
      data-testid={testId}
      onClick={onClick}
      className={`w-full px-4 py-2.5 text-left flex items-center gap-4 text-[15px]
                  transition-colors hover:bg-wa-hover active:bg-wa-active
                  ${destructive ? 'text-red-500' : 'text-wa-text-primary'}`}
    >
      <span className={`shrink-0 ${destructive ? 'text-red-500' : 'text-wa-icon'}`}>{icon}</span>
      <span className="truncate">{label}</span>
    </button>
  );
}
